import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Navigation from "@/components/Navigation";
import { 
  Shield, 
  CheckCircle, 
  AlertCircle, 
  Mail, 
  Phone, 
  ArrowRight,
  Loader2,
  Clock,
  DollarSign
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function KYCVerificationPage({ user, onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [kycStatus, setKycStatus] = useState(null);
  const [limits, setLimits] = useState(null);
  const [verificationMethod, setVerificationMethod] = useState('email');
  const [verificationCode, setVerificationCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    fetchKYCData();
  }, [user]);

  const fetchKYCData = async () => {
    try {
      setLoading(true);
      
      // Fetch KYC status
      const statusRes = await fetch(`${BACKEND_URL}/api/kyc/status/${user.id}`);
      const statusData = await statusRes.json();
      setKycStatus(statusData);
      
      // Fetch limits
      const limitsRes = await fetch(`${BACKEND_URL}/api/kyc/limits/${user.id}`);
      const limitsData = await limitsRes.json();
      setLimits(limitsData.limits);
      
    } catch (error) {
      console.error('Error fetching KYC data:', error);
      toast.error('Failed to load verification status');
    } finally {
      setLoading(false);
    }
  };

  const handleSendCode = async () => {
    try {
      setSending(true);
      
      const res = await fetch(`${BACKEND_URL}/api/kyc/send-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          method: verificationMethod
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setCodeSent(true);
        toast.success(data.message || 'Verification code sent!');
      } else {
        toast.error(data.detail || 'Failed to send code');
      }
      
    } catch (error) {
      console.error('Error sending code:', error);
      toast.error('Failed to send verification code');
    } finally {
      setSending(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    
    try {
      setVerifying(true);
      
      const res = await fetch(`${BACKEND_URL}/api/kyc/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          code: verificationCode
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success('Verification successful!');
        setCodeSent(false);
        setVerificationCode('');
        fetchKYCData(); // Refresh data
      } else {
        toast.error(data.detail || 'Verification failed');
      }
      
    } catch (error) {
      console.error('Error verifying code:', error);
      toast.error('Failed to verify code');
    } finally {
      setVerifying(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
        <Navigation user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      <Navigation user={user} onLogout={onLogout} />
      
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
              <Shield className="w-8 h-8 text-emerald-600" />
              Account Verification
            </h1>
            <p className="text-gray-600">
              Verify your account to unlock higher transfer limits
            </p>
          </div>

          {/* Verification Status Card */}
          <Card className={`mb-6 border-2 ${
            kycStatus?.is_verified 
              ? 'border-emerald-500 bg-emerald-50' 
              : 'border-amber-300 bg-amber-50'
          }`}>
            <CardHeader>
              <div className="flex items-center gap-3">
                {kycStatus?.is_verified ? (
                  <CheckCircle className="w-8 h-8 text-emerald-600" />
                ) : (
                  <AlertCircle className="w-8 h-8 text-amber-600" />
                )}
                <div>
                  <CardTitle className={kycStatus?.is_verified ? 'text-emerald-900' : 'text-amber-900'}>
                    {kycStatus?.is_verified ? 'Account Verified' : 'Verification Required'}
                  </CardTitle>
                  <CardDescription className={kycStatus?.is_verified ? 'text-emerald-700' : 'text-amber-700'}>
                    {kycStatus?.is_verified 
                      ? `Verified on ${new Date(kycStatus.verified_at).toLocaleDateString()}`
                      : 'Verify to unlock higher transfer limits'
                    }
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Limits Card */}
          {limits && (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-emerald-600" />
                  Your Transfer Limits
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Daily Limit */}
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Daily Limit</span>
                      <span className="font-medium">{formatCurrency(limits.daily_limit)}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-emerald-600 h-2 rounded-full transition-all"
                        style={{ width: `${(limits.daily_used / limits.daily_limit) * 100}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500">
                      {formatCurrency(limits.daily_remaining)} remaining today
                    </p>
                  </div>
                  
                  {/* Monthly Limit */}
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Monthly Limit</span>
                      <span className="font-medium">{formatCurrency(limits.monthly_limit)}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all"
                        style={{ width: `${(limits.monthly_used / limits.monthly_limit) * 100}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500">
                      {formatCurrency(limits.monthly_remaining)} remaining this month
                    </p>
                  </div>
                </div>
                
                {/* Threshold Info */}
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-gray-500 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-700">
                        <strong>Verification required for transfers over {formatCurrency(limits.verification_threshold)}</strong>
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Maximum single transfer: {formatCurrency(limits.max_single_transfer)}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Verification Form - Only show if not verified */}
          {!kycStatus?.is_verified && (
            <Card>
              <CardHeader>
                <CardTitle>Verify Your Identity</CardTitle>
                <CardDescription>
                  Choose a verification method to unlock higher limits
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Method Selection */}
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => setVerificationMethod('email')}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      verificationMethod === 'email'
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Mail className={`w-6 h-6 mx-auto mb-2 ${
                      verificationMethod === 'email' ? 'text-emerald-600' : 'text-gray-400'
                    }`} />
                    <p className={`text-sm font-medium ${
                      verificationMethod === 'email' ? 'text-emerald-900' : 'text-gray-600'
                    }`}>Email</p>
                    <p className="text-xs text-gray-500 mt-1">{kycStatus?.email || user?.email}</p>
                  </button>
                  
                  <button
                    onClick={() => setVerificationMethod('phone')}
                    disabled={!kycStatus?.phone}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      verificationMethod === 'phone'
                        ? 'border-emerald-500 bg-emerald-50'
                        : kycStatus?.phone
                          ? 'border-gray-200 hover:border-gray-300'
                          : 'border-gray-100 bg-gray-50 cursor-not-allowed'
                    }`}
                  >
                    <Phone className={`w-6 h-6 mx-auto mb-2 ${
                      verificationMethod === 'phone' ? 'text-emerald-600' : 'text-gray-400'
                    }`} />
                    <p className={`text-sm font-medium ${
                      verificationMethod === 'phone' ? 'text-emerald-900' : 'text-gray-600'
                    }`}>Phone</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {kycStatus?.phone || 'Not added'}
                    </p>
                  </button>
                </div>

                {/* Send Code / Enter Code */}
                {!codeSent ? (
                  <Button 
                    onClick={handleSendCode}
                    disabled={sending}
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                  >
                    {sending ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                    ) : (
                      <>Send Verification Code <ArrowRight className="w-4 h-4 ml-2" /></>
                    )}
                  </Button>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="code">Enter 6-digit code</Label>
                      <Input
                        id="code"
                        type="text"
                        placeholder="000000"
                        maxLength={6}
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                        className="text-center text-2xl tracking-widest mt-2"
                      />
                    </div>
                    
                    <div className="flex gap-3">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setCodeSent(false);
                          setVerificationCode('');
                        }}
                        className="flex-1"
                      >
                        Back
                      </Button>
                      <Button 
                        onClick={handleVerifyCode}
                        disabled={verifying || verificationCode.length !== 6}
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                      >
                        {verifying ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying...</>
                        ) : (
                          'Verify Code'
                        )}
                      </Button>
                    </div>
                    
                    <p className="text-center text-sm text-gray-500">
                      Didn't receive the code?{' '}
                      <button
                        onClick={handleSendCode}
                        disabled={sending}
                        className="text-emerald-600 hover:underline"
                      >
                        Resend
                      </button>
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Benefits of Verification */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="text-lg">Benefits of Verification</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900">Higher Daily Limit</p>
                    <p className="text-sm text-gray-500">Up to $50,000/day</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900">Higher Monthly Limit</p>
                    <p className="text-sm text-gray-500">Up to $200,000/month</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900">Faster Processing</p>
                    <p className="text-sm text-gray-500">Priority transaction handling</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900">Full Platform Access</p>
                    <p className="text-sm text-gray-500">All features unlocked</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
